import { gql } from "config/graphql";
import {
  BeneficiaryResponsibility,
  ContactType,
  IAddress,
  IBeneficiary,
  IBeneficiaryAssignedEntity,
  IContactEntity,
  IContactIndividual,
  InputBeneficiaryAssignedEntityType,
  MutationResponse,
  SuccessResponse,
} from "config/types";
import { BENEFICIARY_FIELDS } from "gql/fragments/beneficiary";
import { getApolloClient } from "store/apollo";
import { createClientMutationId } from "utils/clientMutationId";
import { assertDefined } from "utils/error";

export interface ICreateBeneficiaryInput {
  type: ContactType;
  contact: Partial<IContactEntity> | Partial<IContactIndividual>;
  address: IAddress;
  responsibility: BeneficiaryResponsibility;
  assignedEntity: Omit<IBeneficiaryAssignedEntity, "id" | "entityType">;
  entityType: InputBeneficiaryAssignedEntityType;
}

interface ICreateBeneficiaryData {
  createBeneficiary: {
    beneficiary: IBeneficiary;
  };
}

export const createBeneficiaryMutation: MutationResponse<
  ICreateBeneficiaryInput,
  ICreateBeneficiaryData
> = async (input: ICreateBeneficiaryInput) => {
  const client = getApolloClient();
  const { type, contact, address, responsibility, assignedEntity, entityType } = input;

  const mutation = gql`
    ${BENEFICIARY_FIELDS}
    mutation createBeneficiary($input: CreateBeneficiaryInput!) {
      createBeneficiary(input: $input) {
        beneficiary {
          ...BeneficiaryFields
        }
      }
    }
  `;

  const { data } = await client.mutate<ICreateBeneficiaryData>({
    variables: {
      input: {
        responsibility,
        contact: {
          address,
          [type]: contact,
        },
        assignedEntity: {
          [entityType]: assignedEntity,
        },
        clientMutationId: createClientMutationId(),
      },
    },
    mutation,
  });
  return { data: assertDefined(data) };
};

export interface IUpdateBeneficiaryInput {
  type: ContactType;
  contact: Partial<IContactEntity> | Partial<IContactIndividual>;
  address: IAddress;
  beneficiaryId: string;
}

interface IUpdateBeneficiaryData {
  updateBeneficiary: {
    beneficiary: IBeneficiary;
  };
}

export const updateBeneficiaryMutation: MutationResponse<
  IUpdateBeneficiaryInput,
  IUpdateBeneficiaryData
> = async (input: IUpdateBeneficiaryInput) => {
  const client = getApolloClient();
  const { type, contact, address, beneficiaryId } = input;

  const mutation = gql`
    ${BENEFICIARY_FIELDS}
    mutation updateBeneficiary($input: UpdateBeneficiaryInput!) {
      updateBeneficiary(input: $input) {
        beneficiary {
          ...BeneficiaryFields
        }
      }
    }
  `;

  const { data } = await client.mutate<IUpdateBeneficiaryData>({
    variables: {
      input: {
        beneficiaryId,
        contact: {
          address,
          [type]: contact,
        },
      },
    },
    mutation,
  });
  return { data: assertDefined(data) };
};

export interface ISendBeneficiaryPacketInput {
  beneficiaryId: string;
}

interface ISendBeneficiaryPacketData {
  sendBeneficiaryPacket: SuccessResponse;
}

export const sendBeneficiaryPacketMutation: MutationResponse<
  ISendBeneficiaryPacketInput,
  ISendBeneficiaryPacketData
> = async (input: ISendBeneficiaryPacketInput) => {
  const client = getApolloClient();

  const mutation = gql`
    mutation sendBeneficiaryPacket($input: SendBeneficiaryPacketInput!) {
      sendBeneficiaryPacket(input: $input) {
        response {
          success
        }
      }
    }
  `;

  const { data } = await client.mutate<ISendBeneficiaryPacketData>({
    mutation,
    variables: { input },
  });
  return { data: assertDefined(data) };
};

export interface IDestroyBeneficiaryInput {
  beneficiaryId: string;
  promoteBackup?: boolean;
  destroyBackup?: boolean;
}

interface IDestroyBeneficiaryData {
  destroyBeneficiary: SuccessResponse;
}

export const destroyBeneficiaryMutation: MutationResponse<
  IDestroyBeneficiaryInput,
  IDestroyBeneficiaryData
> = async (input: IDestroyBeneficiaryInput) => {
  const client = getApolloClient();
  const { beneficiaryId, promoteBackup = false, destroyBackup = false } = input;

  const mutation = gql`
    mutation destroyBeneficiary($input: DestroyBeneficiaryInput!) {
      destroyBeneficiary(input: $input) {
        response {
          success
        }
      }
    }
  `;

  const { data } = await client.mutate<IDestroyBeneficiaryData>({
    variables: {
      input: {
        beneficiaryId,
        promoteBackup,
        destroyBackup,
        clientMutationId: createClientMutationId(),
      },
    },
    mutation,
  });
  return { data: assertDefined(data) };
};

export interface BeneficiaryInputAttributes {
  beneficiaryId: string;
  allocation: number;
}

interface IUpdateBeneficiaryAllocationData {
  updateBeneficiaryAllocation: SuccessResponse<{ status: string }>;
}

export const updateBeneficiaryAllocationMutation: MutationResponse<
  BeneficiaryInputAttributes[],
  IUpdateBeneficiaryAllocationData
> = async (beneficiaryAllocations: BeneficiaryInputAttributes[]) => {
  const client = getApolloClient();

  const mutation = gql`
    mutation UpdateBeneficiaryAllocation($input: UpdateBeneficiaryAllocationInput!) {
      updateBeneficiaryAllocation(input: $input) {
        response {
          status
          success
        }
      }
    }
  `;

  const { data } = await client.mutate<IUpdateBeneficiaryAllocationData>({
    variables: {
      input: {
        beneficiaryAllocations,
        clientMutationId: createClientMutationId(),
      },
    },
    mutation,
  });
  return { data: assertDefined(data) };
};
